import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { eventLabels, type BootstrapData, type Creative, type Hypothesis, type WorkspaceEvent } from "@lovmia/shared";
import { api, ApiError, loadBootstrap, loadCreative, loadEvents, loadHypothesis, uploadCharacterImage, uploadCreativeVideo } from "./api";

interface Toast {
  id: number;
  title: string;
  detail?: string;
  tone?: "default" | "success" | "danger";
}

interface AppState {
  data: BootstrapData;
  refresh: () => Promise<unknown>;
  act: <T = unknown>(path: string, init?: RequestInit, success?: string) => Promise<T>;
  notify: (title: string, detail?: string, tone?: Toast["tone"]) => void;
  /** Состояние живого канала: "live" — поток открыт, "polling" — фолбэк опросом. */
  liveState: LiveState;
}

const StateContext = createContext<AppState | null>(null);

export function AppStateProvider({ children, data }: { children: ReactNode; data: BootstrapData }) {
  const queryClient = useQueryClient();
  const notify = useToastActions();
  // После своей мутации — обычная инвалидация: летящий запрос мог уйти до
  // правки, его ответ устарел, поэтому его обрываем и спрашиваем заново.
  const refresh = useCallback(() => invalidateWorkspace(queryClient), [queryClient]);
  const act = useCallback(async <T,>(path: string, init?: RequestInit, success?: string) => {
    const result = await api<T>(path, init);
    await refresh();
    if (success) notify(success, undefined, "success");
    return result;
  }, [refresh, notify]);
  // События ленты — склеенной пачкой и без обрыва летящего запроса: на доске
  // в сотни крео каждый лишний bootstrap — секунды работы сервера.
  const refreshQuiet = useCallback(() => invalidateWorkspace(queryClient, { cancelRefetch: false }), [queryClient]);
  const liveState = useWorkspaceEvents({ selfId: data.user.id, refresh: refreshQuiet, notify });
  const value = useMemo(() => ({ data, refresh, act, notify, liveState }), [data, refresh, act, notify, liveState]);
  return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
}

export type LiveState = "connecting" | "live" | "polling";

/**
 * Доска и детали модалок — одно состояние: bootstrap отдаёт облегчённые крео,
 * а сценарии, шаблоны и комментарии живут в `["creative", id]` и
 * `["hypothesis", id]`. Инвалидируются они вместе, иначе модалка покажет
 * старый сценарий рядом со свежими цифрами. Неактивные детали только
 * помечаются устаревшими и догрузятся при следующем открытии.
 */
function invalidateWorkspace(queryClient: QueryClient, options?: { cancelRefetch?: boolean }) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["bootstrap"] }, options),
    queryClient.invalidateQueries({ queryKey: ["creative"] }, options),
    queryClient.invalidateQueries({ queryKey: ["hypothesis"] }, options)
  ]);
}

/** Склейка событий ленты: тишина 1 с — обновляем; поток не стихает — всё равно не реже раза в 3 с. */
const EVENT_DEBOUNCE_MS = 1_000;
const EVENT_MAX_WAIT_MS = 3_000;

/** Заголовок и подпись тоста по событию ленты. */
function describeEvent(event: WorkspaceEvent) {
  const who = event.source === "mcp" ? "Claude" : event.source === "system" ? "Синк метрик" : event.actorName ?? "Кто-то";
  const payload = event.payload as { title?: unknown; statement?: unknown; text?: unknown; name?: unknown };
  const subject = [payload.title, payload.statement, payload.text, payload.name].find((value) => typeof value === "string" && value.trim()) as string | undefined;
  const detail = [event.subjectId, subject && subject.length > 90 ? `${subject.slice(0, 90)}…` : subject].filter(Boolean).join(" · ");
  return { title: `${who}: ${eventLabels[event.event] ?? event.event}`, detail: detail || undefined };
}

/**
 * Живая доска. Поток SSE держит вкладку в курсе чужих действий — прежде всего
 * гипотез, которые Claude создаёт через MCP, пока человек смотрит на доску.
 *
 * Курсор `seq` копится в ref: и переподключение потока, и фолбэк-опрос
 * продолжают с последнего увиденного события, поэтому пропущенное доезжает,
 * а увиденное не показывается дважды. Если поток не поднялся (прокси режет
 * text/event-stream, вкладка в фоне у части браузеров), включается опрос раз в
 * 20 секунд — доска остаётся живой, просто с большей задержкой.
 */
function useWorkspaceEvents({ selfId, refresh, notify }: {
  selfId: string;
  refresh: () => Promise<unknown>;
  notify: (title: string, detail?: string, tone?: Toast["tone"]) => void;
}) {
  const [liveState, setLiveState] = useState<LiveState>("connecting");
  const cursor = useRef(0);
  // Свежие колбэки без переподключения потока на каждый ререндер.
  const sink = useRef({ refresh, notify, selfId });
  sink.current = { refresh, notify, selfId };

  // Trailing debounce с потолком: пачка событий от Claude (волна из десятка
  // крео) — один запрос bootstrap, а не десять.
  const pending = useRef<{ timer: number | null; first: number | null }>({ timer: null, first: null });
  const flush = useCallback(() => {
    if (pending.current.timer !== null) window.clearTimeout(pending.current.timer);
    pending.current = { timer: null, first: null };
    void sink.current.refresh();
  }, []);
  const scheduleRefresh = useCallback(() => {
    const now = Date.now();
    const first = pending.current.first ?? now;
    if (pending.current.timer !== null) window.clearTimeout(pending.current.timer);
    const wait = Math.max(0, Math.min(EVENT_DEBOUNCE_MS, first + EVENT_MAX_WAIT_MS - now));
    pending.current = { first, timer: window.setTimeout(flush, wait) };
  }, [flush]);
  useEffect(() => () => { if (pending.current.timer !== null) window.clearTimeout(pending.current.timer); }, []);

  const handle = useCallback((events: WorkspaceEvent[]) => {
    const fresh = events.filter((event) => event.seq > cursor.current);
    if (!fresh.length) return;
    cursor.current = Math.max(cursor.current, ...fresh.map((event) => event.seq));
    scheduleRefresh();
    // Свои же действия уже подтверждены тостом на месте — их не повторяем.
    const foreign = fresh.filter((event) => event.actorId !== sink.current.selfId || event.source === "mcp");
    if (!foreign.length) return;
    if (foreign.length > 3) {
      sink.current.notify("Доска обновилась", `${foreign.length} изменений от команды`, "success");
      return;
    }
    for (const event of foreign) {
      const { title, detail } = describeEvent(event);
      sink.current.notify(title, detail, "success");
    }
  }, [scheduleRefresh]);

  useEffect(() => {
    let stream: EventSource | null = null;
    let pollTimer: number | null = null;
    let disposed = false;
    let failures = 0;

    const poll = async () => {
      try {
        const result = await loadEvents(cursor.current);
        // Первый опрос без курсора поднимает всю ленту — она уже учтена в
        // bootstrap, поэтому просто встаём на её край, без тостов о прошлом.
        if (!cursor.current) cursor.current = result.cursor;
        else handle(result.events);
      } catch { /* сеть отвалилась — следующий тик попробует снова */ }
    };

    const startPolling = () => {
      if (disposed || pollTimer !== null) return;
      setLiveState("polling");
      pollTimer = window.setInterval(poll, 20_000);
      void poll();
    };

    const stopPolling = () => {
      if (pollTimer === null) return;
      window.clearInterval(pollTimer);
      pollTimer = null;
    };

    const connect = () => {
      if (disposed) return;
      stream = new EventSource(`/api/events/stream${cursor.current ? `?since=${cursor.current}` : ""}`);
      stream.addEventListener("ready", (message) => {
        failures = 0;
        stopPolling();
        setLiveState("live");
        const payload = JSON.parse((message as MessageEvent<string>).data) as { cursor: number };
        if (!cursor.current) cursor.current = payload.cursor;
      });
      stream.addEventListener("change", (message) => {
        handle([JSON.parse((message as MessageEvent<string>).data) as WorkspaceEvent]);
      });
      stream.onerror = () => {
        // EventSource переподключается сам; после трёх неудач подряд считаем,
        // что поток здесь не живёт, и переходим на опрос.
        failures += 1;
        if (failures >= 3) {
          stream?.close();
          stream = null;
          startPolling();
        } else {
          setLiveState("connecting");
        }
      };
    };

    if (typeof EventSource === "undefined") startPolling(); else connect();

    // Возврат на вкладку после сна: за это время поток мог тихо умереть, а
    // события — накопиться. Дешёвая проверка курсором закрывает разрыв.
    const onVisible = () => { if (document.visibilityState === "visible") void poll(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisible);
      stopPolling();
      stream?.close();
    };
  }, [handle]);

  return liveState;
}

export function useAppState() {
  const value = useContext(StateContext);
  if (!value) throw new Error("AppStateProvider missing");
  return value;
}

/**
 * Облегчённый bootstrap (`slim: true`) приходит без `statement` у крео — оно
 * лежит в гипотезе и на 800 крео повторялось бы сотнями килобайт. Возвращаем
 * поле здесь, в одном месте, чтобы остальной код видел прежнюю форму данных.
 */
function hydrateBootstrap(data: BootstrapData): BootstrapData {
  for (const hypothesis of data.hypotheses) {
    for (const creative of hypothesis.creatives) {
      if (creative.statement == null) creative.statement = hypothesis.statement;
    }
  }
  return data;
}

/** Облегчённый ли bootstrap пришёл: тогда сценарии и шаблоны модалка берёт из деталей. */
export function isSlimBootstrap(data: BootstrapData) {
  return (data as BootstrapData & { slim?: boolean }).slim === true;
}

/**
 * Кеш доски в браузере. Открытие прода упиралось в bootstrap на десятки
 * секунд, а последнюю увиденную доску можно показать мгновенно: свежие данные
 * догружаются в фоне. Ключ привязан к пользователю (чужая доска на общем
 * компьютере не всплывёт) и к версии схемы (старая форма данных не доедет до
 * нового кода). Указатель на последнего пользователя нужен, потому что до
 * ответа сервера неизвестно, кто открыл вкладку.
 */
const BOOTSTRAP_CACHE_PREFIX = "creo-loop:bootstrap:v1:";
const BOOTSTRAP_CACHE_USER = "creo-loop:bootstrap:user";

interface BootstrapCache { savedAt: number; data: BootstrapData }

function readBootstrapCache(): BootstrapCache | null {
  try {
    const userId = localStorage.getItem(BOOTSTRAP_CACHE_USER);
    if (!userId) return null;
    const raw = localStorage.getItem(`${BOOTSTRAP_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BootstrapCache;
    if (typeof parsed?.savedAt !== "number" || parsed.data?.user?.id !== userId) return null;
    return parsed;
  } catch { return null; }
}

function writeBootstrapCache(data: BootstrapData) {
  const key = `${BOOTSTRAP_CACHE_PREFIX}${data.user.id}`;
  try {
    localStorage.setItem(BOOTSTRAP_CACHE_USER, data.user.id);
    localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data } satisfies BootstrapCache));
  } catch {
    // Квота кончилась или приватное окно: прошлый снимок не оставляем — он
    // показывался бы всё более старым.
    try { localStorage.removeItem(key); } catch { /* хранилище недоступно вовсе */ }
  }
}

/** Выход и 401: снимок доски не должен пережить сессию. */
export function clearBootstrapCache() {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(BOOTSTRAP_CACHE_PREFIX)) localStorage.removeItem(key);
    }
    localStorage.removeItem(BOOTSTRAP_CACHE_USER);
  } catch { /* хранилище недоступно — чистить нечего */ }
}

async function fetchBootstrap() {
  try {
    // Без `cache: "no-store"`: сервер отдаёт ETag, и на 304 браузер сам
    // подставит прошлое тело — повторное открытие не тянет мегабайты.
    const data = await loadBootstrap();
    writeBootstrapCache(data);
    return hydrateBootstrap(data);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) clearBootstrapCache();
    throw error;
  }
}

const authError = (error: unknown) => error instanceof ApiError && (error.status === 401 || error.status === 403);

export function useBootstrap() {
  // Снимок читаем один раз на монтирование: initialData не должен парсить
  // мегабайты JSON на каждый ререндер.
  const [cache] = useState(readBootstrapCache);
  const query = useQuery({
    queryKey: ["bootstrap"],
    queryFn: fetchBootstrap,
    initialData: cache ? () => hydrateBootstrap(cache.data) : undefined,
    // Снимок всегда устаревший: иначе в первые staleTime после сохранения
    // запроса не будет вовсе, и доска так и останется из кеша. Время снимка
    // для плашки берём из самого кеша.
    initialDataUpdatedAt: cache ? 0 : undefined,
    // Сеть мигнула — пробуем ещё, но не на 401/403: там ответ не изменится.
    retry: (failures, error) => failures < 3 && !authError(error),
    // 1 → 2 → 4 с: сбой сети переживаем быстро, а лежащий сервер не долбим.
    retryDelay: (attempt) => Math.min(1_000 * 2 ** Math.max(0, attempt - 1), 8_000),
    staleTime: 15_000
  });
  // Доска из снимка, пока ни один запрос в этой вкладке не удался.
  // dataUpdatedAt снимка — 0 (см. initialDataUpdatedAt), первый удачный запрос
  // его перезапишет; ошибка — нет, поэтому плашка «сервер не ответил» остаётся.
  const cachedAt = cache && query.dataUpdatedAt === 0 ? cache.savedAt : null;
  return { ...query, cachedAt };
}

/**
 * Полные записи для модалок. При облегчённом bootstrap сценарий, шаблон,
 * комментарий и разметка есть только здесь; на старом сервере (`slim` не
 * пришёл) запроса нет вовсе — модалка работает по bootstrap, как раньше.
 */
export function useCreativeDetail(creativeId: string, enabled: boolean) {
  return useQuery<Creative>({ queryKey: ["creative", creativeId], queryFn: () => loadCreative(creativeId), enabled, staleTime: 15_000, retry: 1 });
}

export function useHypothesisDetail(hypothesisId: string, enabled: boolean) {
  return useQuery<Hypothesis>({ queryKey: ["hypothesis", hypothesisId], queryFn: () => loadHypothesis(hypothesisId), enabled, staleTime: 15_000, retry: 1 });
}

/**
 * Загрузка видео живёт отдельно от `act`: файл идёт прямо в бакет, а не через
 * наш API, поэтому у неё свой прогресс и свои ошибки хранилища.
 */
export function useCreativeUpload() {
  const { refresh, notify } = useAppState();
  const [progress, setProgress] = useState<number | null>(null);
  const upload = useCallback(async (creativeId: string, file: File) => {
    setProgress(0);
    try {
      await uploadCreativeVideo(creativeId, file, setProgress);
      await refresh();
      notify(`${creativeId} готов к заливу`, undefined, "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить видео", undefined, "danger");
    } finally {
      setProgress(null);
    }
  }, [refresh, notify]);
  const label = progress == null ? null : progress > 0 && progress < 1 ? `Загружаем… ${Math.round(progress * 100)}%` : "Загружаем…";
  return { upload, progress, label };
}

/**
 * Лист персонажа. Отдельно от загрузки крео по той же причине: байты идут мимо
 * API, поэтому у неё свой прогресс и свои ошибки хранилища. Возвращает флаг
 * успеха — форма создания персонажа должна закрыться только если лист доехал.
 */
export function useCharacterUpload() {
  const { refresh, notify } = useAppState();
  const [progress, setProgress] = useState<number | null>(null);
  const upload = useCallback(async (characterId: string, file: File) => {
    setProgress(0);
    try {
      await uploadCharacterImage(characterId, file, setProgress);
      await refresh();
      return true;
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить лист персонажа", undefined, "danger");
      return false;
    } finally {
      setProgress(null);
    }
  }, [refresh, notify]);
  const label = progress == null ? null : progress > 0 && progress < 1 ? `Загружаем… ${Math.round(progress * 100)}%` : "Загружаем…";
  return { upload, progress, label };
}

const ToastContext = createContext<((title: string, detail?: string, tone?: Toast["tone"]) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const notify = useCallback((title: string, detail?: string, tone: Toast["tone"] = "default") => {
    const id = Date.now() + Math.random();
    setItems((current) => [...current, { id, title, detail, tone }]);
    window.setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), 3600);
  }, []);
  return <ToastContext.Provider value={notify}>
    {children}
    <div className="toast-stack" aria-live="polite">
      {items.map((item) => <div className={`toast toast--${item.tone}`} key={item.id}>
        <span className="toast__dot" />
        <div><strong>{item.title}</strong>{item.detail && <small>{item.detail}</small>}</div>
      </div>)}
    </div>
  </ToastContext.Provider>;
}

export function useToastActions() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("ToastProvider missing");
  return value;
}

export function useApiMutation<TVariables>(options: {
  mutationFn: (variables: TVariables) => Promise<unknown>;
  success?: string;
}) {
  const { refresh, notify } = useAppState();
  return useMutation({
    mutationFn: options.mutationFn,
    onSuccess: async () => { await refresh(); if (options.success) notify(options.success, undefined, "success"); },
    onError: (error: Error) => notify(error.message, undefined, "danger")
  });
}
